export class IssuingClient {
    id: number = 0;
    individualId: number = 0;
    flexCubeBranchCode: string = "";
    clientStatus: IssuingClientStatus = IssuingClientStatus.VerificationRequested;
    responseDt?: Date | null;
    icardClientId: number = 0;
    flexCubeCbsId: string = "";
    requestRaw: string = "";
    responseRaw: string = "";
    name?: string = "";
    email?: string = "";
    responseDecrypted: string = "";
    createDt?: Date | null;
    verifyDt?: Date | null;
    requestDt?: Date | null;
    eventDt?: string | null
}
export class IssuingClientEvent {
    id: number = 0;
    individualId: number = 0;
    flexCubeBranchCode: string | null = null;
    flexCubeCbsId: string | null = null;
    icardClientId: number = 0;

    event?: string | null;
    eventDetails?: string | null;
    eventDt?: string | null;

    requestRaw?: string | null;
    responseRaw?: string | null;
}

export class CardEvent {
    id: number = 0;
    cardId: number = 0;
    individualId: number = 0;

    flexCubeBranchCode?: string | null;
    flexCubeCbsId?: string | null;
    accountNumber?: string | null;

    icardCardId?: number | null;
    icardClientId: number = 0;

    eventDt?: string | null;
    event?: string | null;
    eventDetails?: string | null;

    requestRaw?: string | null;
    responseRaw?: string | null;
}

export class ImportedFiles {
    id: number = 0;
    fileName: string = "";
    requestDt?: Date | null;
}
export interface SettlementCutOff {
    settlementRequestId: string;
    settlementDate: string;
    settlementCycle: number;
    settlementCurrency: string;
    settlementAmount: number;
    debitTurnovers: number;
    creditTurnovers: number;
    recordCount: number;
    id: number;
}
/* ============================
   Root Response
============================ */

export interface SettlementFileResponse {
    file: FileInfo;
    fileType: FileType;
    settlementDetails: SettlementDetail[];
    cardSchemeFeeExchanges: CardSchemeFeeExchange[];
    transactionProcessingRecords: TransactionProcessingRecord[];
}

/* ============================
   Enums & Literal Types
============================ */

export type FileType = "STTL";

export enum EventType {
    AD = "AD",
    AU = "AU",
}

export enum SettlementSign {
    Credit = "C",
    Debit = "D",
}

/**
 * ISO 4217 numeric currency code.
 * Example: 978 = EUR
 */
export type IsoNumericCurrencyCode = `${number}`;

/* ============================
   File Info
============================ */

export interface FileInfo {
    fileName: string;
    importDt: Date;
    id: number;
}

/* ============================
   Settlement Details
============================ */

export interface SettlementDetail {
    sftpImportFileId: number;
    recordType: "STTL";
    settlementRequestId: string;
    settlementDate: Date;
    transactionId: string;
    eventType: EventType;
    settlementAmount: number;
    settlementCurrency: IsoNumericCurrencyCode;
    settlementSign: SettlementSign;
    cardId: string;
    id: number;
}

/* ============================
   Currently Empty Structures
   (Prepared for Future Use)
============================ */

export interface CardSchemeFeeExchange {
    id?: number;
    sftpImportFileId?: number;
    // Add fields once structure is known
}

export interface TransactionProcessingRecord {
    id?: number;
    sftpImportFileId?: number;
    // Add fields once structure is known
}

export enum CardStatus {
    Active = "Active",
    Lost = "Lost",
    Stolen = "Stolen",
    Destroyed = "Destroyed",
    Expired = "Expired",
    BlockedFraudSuspected = "BlockedFraudSuspected",
    BlockedByPartner = "BlockedByPartner",
    ProducedAndInactive = "ProducedAndInactive",
}
export enum CLIENT_ACTIONS {
    Activate = "Activate",
    Block = "Block",
    Terminate = "Terminate",
}

export interface DocumentRequest {
    id: number;
    individualId: number;
    flexCubeCbsId: string;
    flexCubeBranchCode: string;
    icardClientId: number;
    documentRequestId: number;
    documentRequesTypetId: number;
    icardNote: string;
    documentRequestStatus: DocumentRequestStatus;
    documentRequestDt: string; // ISO date string
    documentProvidedDt: string; // ISO date string
    files: DocumentRequestFile[];
}

export interface DocumentRequestFile {
    id: number;
    icardClientDocumentRequestId: number;
    fileName: string;
    fileStorageId: string;
    icardFileId: string;
}

export enum DocumentRequestStatus {
    "Requested" = "Requested",
    "Provided" = "Provided",
    "Rejected" = "Rejected",
    "Pending" = "Pending",
}

export interface CardInfo {
    cardId: number;
    cardStatus: CardStatus;
    cardPlasticType: string;
    maskedPan: string;
    expiryDate: string;
    // Design code chosen in the wallet at order time (TNPDEVAPPS-2040 / TNPDEVAPPS-2053). Absent on
    // older backends and older cards, which are Standard.
    cardDesign?: string | null;
}

export enum CardDesign {
    Standard = "Standard",
    RoseGold = "RoseGold",
}

const CARD_DESIGN_LABELS: Record<string, string> = {
    [CardDesign.Standard]: "Standard",
    [CardDesign.RoseGold]: "Rose Gold",
};

/** Friendly design label; an unknown code is shown as sent, and no design means Standard. */
export const getCardDesignLabel = (code?: string | null): string =>
    code ? CARD_DESIGN_LABELS[code] ?? code : CARD_DESIGN_LABELS[CardDesign.Standard];

export interface PayloadCardDetails {
    IndividualId: number;
    CardId: number;
}

export interface PayloadCardAction {
    individualId: number;
    cardId: number;
    cardStatus?: string;
}

export enum IssuingClientStatus {
    // Onboarding / KYC
    VerificationRequested = "Verification Requested",
    VerificationInProgress = "Verification In Progress",
    Verified = "Verified",
    SentForManualProcessing="Sent For Manual Processing",
    Rejected = "Rejected",
    SentToIcard = "Sent To Icard",
    New = "New",

    // Active lifecycle
    Active = "Active",
    Suspended = "Suspended",
    Pending = "Pending",

    // Risk / Compliance
    Blocked = "Blocked",
    BlockedFraudSuspected = "Blocked (Fraud Suspected)",

    // End of lifecycle
    Closed = "Closed",
}
export enum ClientAction {
    Activate = "Activate",
    Block = "Block",
    Terminate = "Terminate",
}
export const CHANGE_STATUS_REASON = {
    AsPerApplicantInstruction: "AsPerApplicantInstruction",
    LinkedToBlacklistedAccounts: "LinkedToBlacklistedAccounts",
    AcceptancePolicyBreach: "AcceptancePolicyBreach",
    NoBusinessRegistration: "NoBusinessRegistration",
    WrongCountryOfApplication: "WrongCountryOfApplication",
    InactiveApplicant: "InactiveApplicant",
    RegulatoryReasonsThirdParty: "RegulatoryReasonsThirdParty",
    AsPerClientInstruction: "AsPerClientInstruction",
    ConfirmedFraud: "ConfirmedFraud",
    LinksToBlacklistedAccounts2: "LinksToBlacklistedAccounts2",
    AcceptancePolicyBreach2: "AcceptancePolicyBreach2",
    RegulatoryReasonsAuthority: "RegulatoryReasonsAuthority",
    BlacklistedCustomer: "BlacklistedCustomer",
    DissolvedOrNonexistent: "DissolvedOrNonexistent",
    BankruptcyOrLiquidation: "BankruptcyOrLiquidation",
    ReputationalRisk: "ReputationalRisk",
    InactiveCustomerNoBalance: "InactiveCustomerNoBalance",
    OutsideEEAOrSwitzerland: "OutsideEEAOrSwitzerland",
    SuspendedLicense: "SuspendedLicense",
    AccountTakeover: "AccountTakeover",
    MuleAccount: "MuleAccount",
    DuplicatedAccount: "DuplicatedAccount",
    SuspectedFraudulentActivity: "SuspectedFraudulentActivity",
    RiskOrComplianceConsiderations: "RiskOrComplianceConsiderations",
    Other: "Other",
    FreezingOrder: "FreezingOrder",
    TechnicalIssue: "TechnicalIssue",
    LostCredentialsOrPhone: "LostCredentialsOrPhone",
    SuspiciousLinkedCard: "SuspiciousLinkedCard",
    NegativeBalance: "NegativeBalance",
    IncompleteKyc: "IncompleteKyc",
    ExcessiveFraudTransactions: "ExcessiveFraudTransactions",
    ExcessiveDisputedTransactions: "ExcessiveDisputedTransactions",
    SuspiciousLinkedCardPickUp: "SuspiciousLinkedCardPickUp",
    UnpaidFees: "UnpaidFees",
} as const;
export enum CardPlasticType {
    Physical = "Physical",
    Virtual = "Virtual",
}

export enum ICardStatus {
    BlockedByCardholder = "BlockedByCardholder",
    Active = "Active",
}
